import React from 'react';
import translate from '@/utils/translate';
import type {CampaignProps, CampaignPaidGigBriefItem} from '@/types/campaign';
import BriefField from '../../shared/BriefField';
import BriefItemHtml from '../../shared/BriefItemHtml';
import BriefSection from '../../shared/BriefSection';
import DividedStack from '../../shared/DividedStack';
import QuestionsForCreator from '../../shared/QuestionsForCreator';
import TrackingUrlItem from '../../shared/TrackingUrlItem';
import {
  getBriefItemByTitle,
  type SectionsToHide,
} from '../../utils/briefItemFilters';
import styles from './AdditionalInformation.module.scss';

interface AdditionalInformationProps {
  campaign: CampaignProps;
  briefItems: CampaignPaidGigBriefItem[];
  sectionsToHide: SectionsToHide;
}

function AdditionalInformation({
  campaign,
  briefItems,
  sectionsToHide,
}: AdditionalInformationProps) {
  const brandStyleItem = getBriefItemByTitle(briefItems, 'Brand style guide');
  const eligibilityItem = getBriefItemByTitle(
    briefItems,
    'Eligibility requirements'
  );
  const {questions, campaignTrackingUrls} = campaign;
  const showUrl =
    !sectionsToHide.urlToPromote && (campaignTrackingUrls?.length ?? 0) > 0;

  const questionsWithBody = questions?.filter((q) => q.body) ?? [];

  if (
    !brandStyleItem &&
    !eligibilityItem &&
    !questionsWithBody.length &&
    !showUrl
  ) {
    return null;
  }

  const brandStyleRow = brandStyleItem?.contentHtml ? (
    <BriefField label={translate('Brand style guide')}>
      <BriefItemHtml contentHtml={brandStyleItem.contentHtml} />
    </BriefField>
  ) : null;

  const eligibilityRow = eligibilityItem?.contentHtml ? (
    <BriefField label={translate('Eligibility requirements')}>
      <BriefItemHtml contentHtml={eligibilityItem.contentHtml} />
    </BriefField>
  ) : null;

  const questionsRow = questionsWithBody.length ? (
    <QuestionsForCreator questions={questions!} />
  ) : null;

  const urlRow = showUrl ? (
    <BriefField label={translate('Custom URL for posting')}>
      <div className={styles.stack}>
        {campaignTrackingUrls?.map((trackingUrl) => (
          <TrackingUrlItem
            key={trackingUrl.id ?? trackingUrl.url}
            trackingUrl={trackingUrl}
          />
        ))}
      </div>
    </BriefField>
  ) : null;

  return (
    <BriefSection title={translate('Additional information')}>
      <DividedStack
        items={[brandStyleRow, eligibilityRow, questionsRow, urlRow]}
      />
    </BriefSection>
  );
}

export default AdditionalInformation;
